import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AtSign, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { VariableGroupHeading } from './variable-icons';
import { VariableRow } from './variable-row';
import type { IVariableGroup } from './types';

interface VariableInsertMenuProps {
    readonly groups: readonly IVariableGroup[];
    readonly onInsert: (token: string) => void;
    readonly disabled?: boolean;
    /**
     * Icon-only ghost trigger for a composer toolbar row, matching the shape the
     * emoji and attachment buttons use. Labelled outline button otherwise.
     */
    readonly compact?: boolean;
    /**
     * Data attributes stamped on the portalled panel, for a host whose
     * outside-click detector whitelists its own overlays by selector — the email
     * builder marks every portal of its own with `data-email-builder-overlay`,
     * and without it, opening this menu deselects the block being edited.
     */
    readonly contentAttributes?: Readonly<Record<`data-${string}`, string>>;
    /**
     * Overrides the trigger button text (default: "Variables"). Some surfaces
     * — e.g. the WhatsApp compose/workflow forms — use "Personalize" as the
     * product term instead, so the label is caller-controlled rather than
     * forked. In `compact` mode it is the tooltip + `aria-label` text.
     */
    readonly label?: string;
}

/**
 * "Variables" picker — grouped, searchable, and written for a person rather
 * than for whoever wrote the token.
 *
 * Built on Popover + Command rather than DropdownMenu because a dropdown owns
 * the keyboard for its own type-to-jump behaviour, which fights a real search
 * box. Command also gives arrow-key navigation and filtering for free; each row
 * exposes its label, token and description as the searchable value so typing
 * either the human name or the raw path finds it.
 */
export function VariableInsertMenu({
    groups,
    onInsert,
    disabled,
    compact,
    contentAttributes,
    label: labelOverride,
}: VariableInsertMenuProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const label = labelOverride ?? t('variables.insertButton', 'Variables');

    function handleInsert(token: string) {
        setOpen(false);
        onInsert(token);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {compact ? (
                // Tooltip OUTSIDE the popover trigger, never the other way
                // round: `asChild` slots props onto its single child, and
                // `Tooltip` is Radix's Root — a context provider, not a DOM
                // node — so a trigger wrapping it has nothing to attach the
                // click handler or ref to, and the menu never opens. Both
                // `asChild` layers have to compose down onto the real Button.
                // Own `TooltipProvider`, the same guard `PermissionButton`
                // carries: the app mounts one at the root, but a consumer
                // rendered into a portal or a test harness that does not is
                // otherwise a hard crash ("`Tooltip` must be used within
                // `TooltipProvider`") on a purely decorative label. Nesting
                // providers is supported and cheap.
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    disabled={disabled}
                                    aria-label={label}
                                    className="text-text-secondary"
                                >
                                    <AtSign className="size-4" />
                                </Button>
                            </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent>{label}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" disabled={disabled}>
                        <AtSign className="size-4" />
                        {label}
                        <ChevronDown className="size-3.5" />
                    </Button>
                </PopoverTrigger>
            )}
            <PopoverContent
                align="start"
                // Bounded, and bounded by the space that actually exists: Radix
                // measures the gap between the trigger and the viewport edge
                // into `--radix-popover-content-available-height`, so the popup
                // can never grow past the screen and strand the rows at the
                // bottom of a long list.
                className="flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-96 flex-col p-0"
                // The picker is often opened from inside a node card or a
                // portalled editor toolbar; a click here must not bubble out and
                // be read as an outside click by the host.
                onClick={(event) => event.stopPropagation()}
                {...contentAttributes}
                // Opened from a drawer or dialog, the host's scroll lock listens
                // on `document` and cancels any wheel/touch scroll whose target
                // sits outside its own content node — and this popup is portalled
                // to <body>, so it always does. Stopping the event here keeps it
                // from reaching that listener, and the list scrolls natively.
                // `ComboboxContent` carries the same guard for the same reason.
                onWheel={(event) => event.stopPropagation()}
                onTouchMove={(event) => event.stopPropagation()}
            >
                {/* `min-h-0` on both flex children is what makes the list
                    scroll at all. A flex item defaults to `min-height: auto`,
                    which refuses to shrink below its content — so the list grew
                    to its natural height, `overflow-y-auto` never engaged, and
                    the rows past the fold were simply unreachable. */}
                <Command className="min-h-0 flex-1">
                    <CommandInput
                        placeholder={t('variables.searchPlaceholder', 'Search variables…')}
                    />
                    {/* `overscroll-contain` stops a scroll that hits the end of
                        the list from chaining on to the page behind it. */}
                    <CommandList className="max-h-none min-h-0 flex-1 overscroll-contain">
                        <CommandEmpty>{t('variables.empty', 'No matching variables')}</CommandEmpty>
                        {groups.map((group) => (
                            <CommandGroup
                                key={group.label}
                                // Passed as an ELEMENT rather than styling
                                // `[cmdk-group-heading]` in `ui/command.tsx` —
                                // that file dresses every Command in the app.
                                // The span's own classes beat the wrapper's.
                                heading={
                                    <VariableGroupHeading label={group.label} icon={group.icon} />
                                }
                            >
                                {group.items.map((item) => (
                                    <CommandItem
                                        key={item.token}
                                        // Searchable across all three, so "first",
                                        // "first_name" and "Sarah" all find the row.
                                        value={`${item.label} ${item.token} ${item.description ?? ''}`}
                                        onSelect={() => handleInsert(item.token)}
                                    >
                                        <VariableRow item={item} groupIcon={group.icon} />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
